<template>
  <!-- <div class="map-poi" :class="{ 'is-retina-map': isRetina }"> -->
  <div class="map-poi">
    <div ref="mapEl" class="map-root" />
    <div class="hidden">
      <MapsPopup ref="popupComponent" :feature="activeFeature" />
    </div>

    <MapsActions
      :no-close="noClose"
      @close="close"
      @show-all="showAll"
      @zoom-in="zoomIn"
      @zoom-out="zoomOut"
    />

    <div class="map-active-area" />
    <MapsTrackingPixel />
  </div>
</template>

<script lang="ts" setup>
import '~/assets/css/dynamic/map.css'
import {
  CENTER_ZUERICH,
  INITIAL_ZOOM,
  MapMarker,
  MAX_ZOOM,
  type MapPoiFeature,
} from '~/helpers/maps'
import MapsPopup from './../Popup/index.vue'
import 'leaflet.markercluster'
import L, { type LatLng, type Map } from 'leaflet'

const props = defineProps<{
  features: MapPoiFeature[]
  noClose?: boolean
  noRouteUpdate?: boolean
}>()

let setActiveTimeout: null | number = null
let mapViewChangeTimeout: null | number = null

const route = useRoute()
const router = useRouter()

const emit = defineEmits(['close'])

const zoomQuery = useQueryString('zoom', '')
const centerQuery = useQueryString('center', '')
const activeId = useQueryString('id', '')

const mapEl = ref<HTMLElement>()
const popupComponent = ref<InstanceType<typeof MapsPopup> | null>(null)
let layers: MapMarker[] = []

const popup = new L.Popup({
  maxWidth: 16 * 20,
  minWidth: 16 * 20,
  autoPan: true,
  autoPanPaddingTopLeft: [16, 50],
})

const markers = L.markerClusterGroup({
  chunkedLoading: true,
  showCoverageOnHover: false,
  maxClusterRadius: 100,
  iconCreateFunction(cluster) {
    const markers = cluster.getAllChildMarkers()
    let n = 0
    for (let i = 0; i < markers.length; i++) {
      /* eslint-disable  @typescript-eslint/no-explicit-any */
      n += (markers[i] as any).number || 1
    }
    return L.divIcon({
      html: `<span>${n.toString()}</span>`,
      className: 'map-poi-cluster',
      iconSize: L.point(48, 48),
    })
  },
})

const { withMap, zoomOut, zoomIn } = useMap(mapEl, {
  init: (map) => {
    map.addLayer(markers)
    updateMarkers()
    setInitialView(map)

    const content = popupComponent.value?.getElement()
    if (content) {
      popup.setContent(content)
    }

    map.on('popupclose', function () {
      setActiveId()
    })

    map.on('zoomend', onMapViewChange)
    map.on('moveend', onMapViewChange)
  },
})

function getInitialState(): {
  nid: string
  zoom: number | null
  center: [number, number] | LatLng | null
} {
  const nid = activeId.value
  const zoom = parseFloat(zoomQuery.value)
  const centerParts = centerQuery.value
    .split(',')
    .map((v) => parseFloat(v))
    .filter((v) => !isNaN(v)) as [number, number]
  const center = centerParts.length === 2 ? centerParts : null

  return { nid, zoom: zoom && !isNaN(zoom) ? zoom : null, center }
}

function setInitialView(map: Map) {
  // Open active marker on page load.
  const state = getInitialState()
  let marker: MapMarker | null = null

  if (state.nid) {
    const layer = layers.find((v) => v.data.id === state.nid)
    if (layer) {
      if (!state.center) {
        state.center = layer.getLatLng()
      }
      if (!state.zoom) {
        state.zoom = MAX_ZOOM
      }

      marker = layer
    }
  }

  const zoom = state.zoom || INITIAL_ZOOM
  const center = state.center || CENTER_ZUERICH

  if (marker) {
    const targetPoint = map.project(marker.getLatLng(), zoom).subtract([0, 16])
    const targetLatLng = map.unproject(targetPoint, zoom)

    map.setView(targetLatLng, zoom)
    marker.openParentCluster()
    setActiveId(marker.data.id)
    nextTick(() => {
      popup.setLatLng(targetLatLng)
      map.openPopup(popup)
    })
  } else {
    map.setView(center, zoom)
    showAll()
  }
}

function onMapViewChange() {
  if (mapViewChangeTimeout) {
    window.clearTimeout(mapViewChangeTimeout)
  }

  mapViewChangeTimeout = window.setTimeout(() => {
    updateRouteQuery(activeId.value)
  }, 500)
}

function updateRouteQuery(id = '', noTrack = false) {
  if (props.noRouteUpdate) {
    return
  }
  withMap((map) => {
    const center = map.getCenter()
    const newQuery: Record<string, string | undefined> = {
      zoom: map.getZoom().toString(),
      center: [center.lat, center.lng].join(','),
      id: id || undefined,
      noTrack: noTrack ? 'true' : undefined,
    }

    router.replace({
      query: {
        ...route.query,
        ...newQuery,
      },
    })
  })
}

function setActiveId(id = '') {
  if (setActiveTimeout) {
    window.clearTimeout(setActiveTimeout)
  }
  activeId.value = id

  setActiveTimeout = window.setTimeout(() => {
    updateRouteQuery(id)
  }, 200)
}

function updateMarkers() {
  markers.clearLayers()
  layers = props.features.map((f) => {
    const icon = L.divIcon({
      className: 'map-poi-marker',
      iconSize: L.point(24, 24),
    })
    const layer = new MapMarker(
      new L.LatLng(f.lat, f.lon),
      {
        title: f.title,
        icon,
      },
      f,
    )
    layer.bindTooltip(f.title, {
      direction: 'top',
      opacity: 1,
      offset: [0, -16],
    })
    layer.on('click', (e) => {
      const target: MapMarker = e.target
      setActiveId(e.target.data.id)
      nextTick(() => {
        popup.setLatLng(target.getLatLng())
        withMap((map) => {
          map.openPopup(popup)
        })
      })
    })
    return layer
  })

  markers.addLayers(layers)
}

const activeFeature = computed(() =>
  props.features.find((v) => v.id === activeId.value),
)

watch(
  () => props.features,
  () => {
    updateMarkers()
  },
)

function showAll() {
  withMap((map) => {
    // No features, nothing to zoom.
    if (props.features.length === 0) {
      return
    }

    // Only one feature: Zoom to feature coordinates.
    if (props.features.length === 1 && props.features[0]) {
      const { lat, lon } = props.features[0]
      map.setView([lat, lon])
      map.setZoom(MAX_ZOOM)
      return
    }

    // Multiple features: Calculate the boundaries of all features and fit the map view.
    const bounds = markers.getBounds()
    if (bounds.isValid()) {
      map.fitBounds(bounds)
    }
  })
}

function close() {
  const destination = route.query.destination
  if (typeof destination === 'string' && destination) {
    router.push({
      path: destination,
    })
    return
  }

  emit('close')
}
</script>

<style>
@import 'leaflet.markercluster/dist/MarkerCluster.css';
@import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
</style>
